// ── Edital verticalizado: store de progresso + helpers ───────────────────────
// Conteúdo em src/editais.js (gerado dos PDFs oficiais), carregado sob demanda.
// O editalStore é singleton (import ES), compartilhado pela aba do edital e pelo Ciclo.
import { useState, useEffect } from "react";
import { loadLS, saveLS } from "./storage";
import { sb } from "./supabase";

export const EDITAL_FROM_CARGO = { "prf": "prf", "pf-agente": "pf-agente", "pf-escrivao": "pf-escrivao", "pf-papiloscopista": "pf-papiloscopista", "pcdf-delegado": "pcdf-delegado", "pcdf-agente": "pcdf-agente" };
// Concursos sem escolha de cargo no Ciclo: o edital vem do próprio concurso
export const EDITAL_FROM_CONTEST = { prf: "prf", pppr: "pppr" };
export const editalLeaves = (topics, out = []) => { for (const x of topics) { if (x.c && x.c.length) editalLeaves(x.c, out); else out.push(x.id); } return out; };
// Assuntos planejados de uma sessão ou de um registro: novos guardam `topics` (vários); antigos, só `topic`.
export const sessTopicIds = s => (s && Array.isArray(s.topics) && s.topics.length ? s.topics : s && s.topic ? [s.topic] : []);
export function cycleEditalId() {
  try { const c = loadLS("sf_activeCycle", null); return (c && (EDITAL_FROM_CARGO[c.cargo] || EDITAL_FROM_CONTEST[c.contest])) || null; } catch { return null; }
}
// Um único "store" do progresso do edital, compartilhado pela aba do edital e pelo Ciclo.
export const editalStore = { uid: null, data: null, subs: new Set(), synced: false, dirty: false, timer: null };
export const EDITAL_EMPTY = () => ({ plans: [], active: null, done: {}, prog: {}, rate: {}, rev: {}, lang: {}, log: [], updatedAt: 0 });
export function editalLS(uid) { return "sf_edital_" + (uid || "anon"); }
export function editalEmit() { editalStore.subs.forEach(fn => { try { fn(editalStore.data); } catch {} }); }
export function editalPush() {
  const { uid } = editalStore;
  if (!uid || !editalStore.synced || !editalStore.dirty) return;
  clearTimeout(editalStore.timer);
  editalStore.timer = setTimeout(async () => {
    if (editalStore.uid !== uid) return;
    editalStore.dirty = false;
    try {
      const { error } = await sb.from("edital_progress").upsert({ user_id: uid, data: editalStore.data, updated_at: new Date().toISOString() }, { onConflict: "user_id" });
      if (error) editalStore.dirty = true;
    } catch { editalStore.dirty = true; }
  }, 700);
}
export function editalInit(uid) {
  if (editalStore.uid === uid && editalStore.data) return;
  editalStore.uid = uid; editalStore.synced = false; editalStore.dirty = false;
  editalStore.data = { ...EDITAL_EMPTY(), ...(loadLS(editalLS(uid), null) || {}) };
  editalEmit();
  if (!uid) return;
  (async () => {
    try {
      const { data: row } = await sb.from("edital_progress").select("data").eq("user_id", uid).maybeSingle();
      if (editalStore.uid !== uid) return;
      const remote = row && row.data;
      const local = editalStore.data;
      if (remote && (remote.updatedAt || 0) > (local.updatedAt || 0)) { editalStore.data = { ...EDITAL_EMPTY(), ...remote }; saveLS(editalLS(uid), editalStore.data); editalEmit(); }
      else if ((local.updatedAt || 0) > ((remote && remote.updatedAt) || 0)) editalStore.dirty = true;
    } catch {}
    if (editalStore.uid === uid) { editalStore.synced = true; editalPush(); }
  })();
}
export function editalUpdate(fn) {
  const next = { ...fn(editalStore.data || EDITAL_EMPTY()), updatedAt: Date.now() };
  editalStore.data = next;
  saveLS(editalLS(editalStore.uid), next);
  editalStore.dirty = true;
  editalEmit();
  editalPush();
}
export function useEditalProgress(uid) {
  editalInit(uid);
  const [data, setData] = useState(editalStore.data);
  useEffect(() => {
    editalInit(uid);
    setData(editalStore.data);
    const fn = d => setData(d);
    editalStore.subs.add(fn);
    return () => { editalStore.subs.delete(fn); };
  }, [uid]);
  return [data || EDITAL_EMPTY(), editalUpdate];
}
// Carrega o conteúdo dos editais uma vez só (arquivo separado, sob demanda)
export let _editaisP = null;
export function loadEditais() { if (!_editaisP) _editaisP = import("../editais").then(m => m.EDITAIS); return _editaisP; }
export function useEditais() {
  const [E, setE] = useState(null);
  useEffect(() => { let a = true; loadEditais().then(x => { if (a) setE(x); }); return () => { a = false; }; }, []);
  return E;
}
// Lista "achatada" dos tópicos (com profundidade), na ordem do edital
export function editalFlat(topics, depth = 0, out = []) { for (const x of topics) { out.push({ node: x, depth }); if (x.c) editalFlat(x.c, depth + 1, out); } return out; }
// Matéria do ciclo → matéria do edital
export const _normName = s => String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z ]/g, " ").replace(/\s+/g, " ").trim();
export const EDITAL_ALIASES = {
  "portugues": "port", "lingua portuguesa": "port",
  "raciocinio logico matematico": ["rlm", "rl"], "raciocinio logico": ["rl", "rlm"],
  "direito constitucional": "const", "nocoes de direito constitucional": "const",
  "direito administrativo": "adm", "nocoes de direito administrativo": "adm",
  "direito penal": ["penal", "penalpp"], "direito processual penal": ["pp", "penalpp"],
  "legislacao especial extravagante": "legesp", "legislacao especial": "legesp",
  "ingles ou espanhol": "le", "lingua estrangeira": "le", "ingles": "le", "espanhol": "le",
  "contabilidade": "contab", "contabilidade geral": "contab",
  "direito civil": "civil", "direito empresarial": "emp", "direito tributario": "trib", "medicina legal": "ml", "direito ambiental": "amb",
  "conhecimentos sobre o distrito federal": "df", "conhecimentos do distrito federal": "df",
  "etica e cidadania": "etica", "legislacao de transito": "transito", "transito": "transito",
};
export function matchEditalDisc(ed, name) {
  if (!ed) return null;
  const n = _normName(name);
  const al = EDITAL_ALIASES[n];
  for (const id of [].concat(al || [])) { const d = ed.disc.find(x => x.id === id); if (d) return d; }
  return ed.disc.find(d => _normName(d.name) === n) || ed.disc.find(d => _normName(d.name).replace(/^nocoes de /, "") === n.replace(/^nocoes de /, "")) || null;
}
export function editalLangTopics(d, data, fam) {
  if (!d.choose) return d.topics;
  const pick = ((data && data.lang) || {})[`${fam}:${d.id}`] || "en";
  return d.topics.filter(x => x.id === pick);
}

// Modos de estudo e de revisão
export const STUDY_MODES = [
  { id: "videoaula", label: "Videoaula", desc: "Assista às videoaulas da sua plataforma.", icon: "m_video" },
  { id: "pdf",       label: "PDF",       desc: "Estude pelo material em PDF.", icon: "m_pdf" },
  { id: "lei",       label: "Letra de lei", desc: "Leia a legislação relacionada ao tema.", icon: "m_lei" },
  { id: "outro",     label: "Outro",     desc: "Outro tipo de material de estudo.", icon: "m_outro" },
  { id: "livre",     label: "Livre",     desc: "Estudar livremente, sem material definido.", icon: "m_livre" },
];
export const REVIEW_MODES = [
  { id: "questoes",   label: "Questões",   desc: "Resolva questões e veja seu desempenho na prática.", icon: "r_questoes" },
  { id: "feynman",    label: "Feynman",    desc: "Explique o conteúdo com suas próprias palavras.", icon: "r_feynman" },
  { id: "flashcards", label: "Flashcards", desc: "Revise com cartões e fixe os pontos principais.", icon: "r_flash" },
  { id: "releitura",  label: "Releitura",  desc: "Releia seu material e aprofunde o entendimento.", icon: "r_releitura" },
];
export const ALL_MODES = [...STUDY_MODES, ...REVIEW_MODES];
export const RATINGS = [
  { v: 1, label: "Muito ruim", desc: "Não consegui compreender bem", icon: "f1", color: "#ef4444" },
  { v: 2, label: "Ruim",       desc: "Tive bastante dificuldade",   icon: "f2", color: "#f97316" },
  { v: 3, label: "Médio",      desc: "Tive algumas dificuldades",   icon: "f3", color: "#eab308" },
  { v: 4, label: "Bom",        desc: "Entendi bem a maior parte",   icon: "f4", color: "#84cc16" },
  { v: 5, label: "Muito bom",  desc: "Dominei com facilidade",      icon: "f5", color: "#22c55e" },
];
